import * as vp from 'vopay';

import { FunctionComponent, useState, useEffect } from 'react';
import moment from 'moment';
import useFetchActivityOverview from '../../../../hooks/useApi/useFetchActivityOverview';
import ActivityMultiSelect from './ActivityMultiSelect/ActivityMultiSelect';
import { useTranslation } from 'react-i18next';

const { SubTitle, Graph, DateRangePicker, Box, NoData } = vp;

type FC = FunctionComponent;

const ActivityOverviewContainer: FC = () => {
  const [timeRange, setTimeRange] = useState<[vp.Timestamp, vp.Timestamp] | undefined>();
  const [activityOverview, fetchActivityOverview] = useFetchActivityOverview({});
  const [labels, setLabels] = useState<string[]>([]);
  const [activeTransactionTypes, setActiveTransactionTypes] = useState<string[]>([]);
  const [transactionTypes, setTransactionTypes] = useState<
    { label: string; active: boolean; color: string; TransactionType: string; onClick: () => void }[]
  >([]);
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const [graphData, setGraphData] = useState<vp.GraphData>({ labels: [], datasets: [] });
  const { t } = useTranslation('translations', { keyPrefix: 'HomePage.ActivityOverviewWidget' });

  const intialTimeRange: [vp.Timestamp, vp.Timestamp] = [
    moment().subtract(1, 'week').valueOf() as vp.Timestamp,
    moment().valueOf() as vp.Timestamp,
  ];

  const handleTabClick = (tab: string) => {
    setActiveTransactionTypes((prevActiveTransactionTypes) => {
      const existingTransactionTypeIndex = prevActiveTransactionTypes.findIndex(
        (activeTransactionType) => activeTransactionType === tab,
      );
      if (existingTransactionTypeIndex === -1) {
        return [...prevActiveTransactionTypes, tab];
      } else {
        return prevActiveTransactionTypes.filter((type) => type !== tab);
      }
    });
  };

  const FormatToolTipTitle = (title: string) => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    });

    return formatter.format(parseFloat(title));
  };

  useEffect(() => {
    const ToDate = moment().format('YYYY-MM-DD');
    const FromDate = moment().subtract(1, 'week').format('YYYY-MM-DD');
    setLabels(GetLabels(FromDate, ToDate));
    fetchActivityOverview({
      ToDate,
      FromDate,
    });
  }, []);

  useEffect(() => {
    if (timeRange) {
      const validDate = moment(timeRange?.[0]).isSameOrBefore(moment(timeRange?.[1]));

      const FromDate = moment(timeRange?.[0]).format('YYYY-MM-DD');
      const ToDate = moment(timeRange?.[1]).format('YYYY-MM-DD');
      setLabels(GetLabels(FromDate, ToDate));
      setHasInvalidEndDate(!validDate);

      if (validDate) {
        fetchActivityOverview({
          ToDate,
          FromDate,
        });
      } else {
        setShouldShowEndDateError(true);
      }
    }
  }, [timeRange]);

  useEffect(() => {
    const uniqueTransactionTypes = [
      ...new Set(Object.values(activityOverview.Statistics || {}).map((stat) => stat.TransactionType)),
    ];

    const types = uniqueTransactionTypes.map((type, index) => ({
      label: type,
      active: true,
      color: generateColor(index, uniqueTransactionTypes.length), // Cycle through the color palette
      TransactionType: type,
      onClick: () => handleTabClick(type),
    }));

    setTransactionTypes(types);
    setActiveTransactionTypes(uniqueTransactionTypes);
  }, [activityOverview]);

  useEffect(() => {
    const processedActivityOverview = ProcessActivityOverview(activityOverview, labels);
    const filteredActivityOverview = FilterActivityOverview(activeTransactionTypes, processedActivityOverview);
    setGraphData(filteredActivityOverview);
  }, [activeTransactionTypes]);

  function ProcessActivityOverview(
    unFormattedStatistics: vp.ActivityOverviewResponse,
    axis: string[],
  ): vp.ActivityOverviewResponse {
    const formattedResponse = { ...unFormattedStatistics };
    const statistics = unFormattedStatistics.Statistics ? Object.values(unFormattedStatistics.Statistics) : [];
    const newStatistics: vp.ActivityOverviewItem[] = [];
    axis.forEach((transactionDate) => {
      Object.values(activeTransactionTypes).forEach((transactionType) => {
        const stat = statistics.find(
          (x) => x.TransactionDate === transactionDate && x.TransactionType === transactionType,
        );
        if (stat) {
          newStatistics.push(stat);
        } else {
          newStatistics.push({
            TransactionDate: transactionDate,
            TransactionType: transactionType,
            Amount: 0,
          });
        }
      });
    });
    formattedResponse.Statistics = { ...newStatistics };
    return formattedResponse;
  }

  const generateColor = (index: number, total: number) => {
    const hue = (index / total) * 360;
    return `hsl(${hue}, 70%, 70%)`;
  };

  function FilterActivityOverview(
    activeTypes: string[],
    unFilteredStatistics: vp.ActivityOverviewResponse,
  ): vp.GraphData {
    const DataSets: vp.DataSet[] = [];
    Object.values(activeTypes).forEach((type) => {
      const TypeStats = Object.values(unFilteredStatistics.Statistics || {}).filter(
        (stat) => type === stat.TransactionType,
      );
      DataSets.push({
        data: TypeStats.reduce((accumulator, current) => [...accumulator, current.Amount], [] as number[]),
        borderColor: transactionTypes.find((t) => t.TransactionType === type)?.color || '',
      });
    });

    return {
      labels,
      datasets: DataSets,
    };
  }

  function GetLabels(startDate: string, stopDate: string): string[] {
    const dateArray = [];
    let currentDate = moment(startDate);
    const stopDateParsed = moment(stopDate);
    while (currentDate <= stopDateParsed) {
      dateArray.push(moment(currentDate).format('YYYY-MM-DD'));
      currentDate = currentDate.add(1, 'days');
    }
    return dateArray;
  }

  return (
    <>
      <Box flx="col" r="lg" pb="sm" pe="lg" ps="sm" background="grey">
        <Box pb="sm" minheight="100px">
          <Box ps="lg" pt="lg" width="30%">
            <SubTitle subTitle={t('title')} variant="bold" />
          </Box>
          <Box jc="fe" pe="lg" pt="lg" width="70%">
            <ActivityMultiSelect items={transactionTypes} />
          </Box>
        </Box>
        <Box jc="fe" pe="lg">
          <DateRangePicker
            onChange={setTimeRange}
            initialTime={intialTimeRange}
            hasInvalidEndDate={hasInvalidEndDate}
            shouldShowEndDateError={shouldShowEndDateError}
            setShouldShowEndDateError={setShouldShowEndDateError}
          />
        </Box>
        {activityOverview.Statistics && Object.values(activityOverview.Statistics).length ? (
          <Graph
            data={graphData}
            formatHeader={FormatToolTipTitle}
            height="400"
            formatLabel={(label: string) => label}
          />
        ) : (
          <NoData />
        )}
      </Box>
    </>
  );
};
export default ActivityOverviewContainer;
